// Sanningskälla för Bildindex 2026 — priser, leveranstider och volym för
// professionell fotografering i Sverige. Läses av sidan
// /branschstudier/bildindex-2026 och av CSV-endpointen
// /api/public/bildindex-2026.csv.

export const BILDINDEX_PATH = "/branschstudier/bildindex-2026";
export const BILDINDEX_TITLE = "Bildindex fotografering Sverige 2026";
export const BILDINDEX_PUBLISHED = "2026-08-31";
export const BILDINDEX_LICENSE = "CC BY 4.0";
export const BILDINDEX_LICENSE_URL =
  "https://creativecommons.org/licenses/by/4.0/";

export const BILDINDEX_METHOD = {
  sampleAssignments: 168,
  period: "januari 2024 – augusti 2026",
  measure: "median samt 10:e–90:e percentilen, exkl. moms",
  marginOfError: "±1 500 kr på paketpriser, ±1 arbetsdag på leveranstider",
  excludes: "resor utanför uppdragsorten, licenser för utomhusreklam och rekvisita",
} as const;

export interface BildindexRow {
  service: string;
  /** Typiskt paketpris exkl. moms, kronor. */
  priceMin: number;
  priceMax: number;
  /** Halvdag/heldag på plats. */
  onSite: string;
  /** Leveranstid på retuscherade bilder, arbetsdagar. */
  leadMin: number;
  leadMax: number;
  /** Antal levererade, retuscherade bilder. */
  delivered: string;
  /** Andel av Fennys levererade fotovolym 2024–2026. */
  volumeShare: number;
}

export const BILDINDEX: BildindexRow[] = [
  { service: "Företagsporträtt (team, studio eller på plats)", priceMin: 9000, priceMax: 22000, onSite: "Halvdag", leadMin: 3, leadMax: 7, delivered: "1–3 per person", volumeShare: 26 },
  { service: "Headshots enskild person", priceMin: 3500, priceMax: 7500, onSite: "1–2 h", leadMin: 2, leadMax: 5, delivered: "3–6", volumeShare: 11 },
  { service: "Produktfoto e-handel", priceMin: 6000, priceMax: 28000, onSite: "Halvdag–heldag", leadMin: 4, leadMax: 10, delivered: "15–120", volumeShare: 18 },
  { service: "Kampanj- och livsstilsfoto", priceMin: 25000, priceMax: 85000, onSite: "1–2 dagar", leadMin: 7, leadMax: 15, delivered: "20–40", volumeShare: 14 },
  { service: "Eventfoto / konferens", priceMin: 8000, priceMax: 24000, onSite: "Halvdag–heldag", leadMin: 1, leadMax: 5, delivered: "60–200", volumeShare: 12 },
  { service: "Interiör- och arkitekturfoto", priceMin: 9000, priceMax: 30000, onSite: "Halvdag–heldag", leadMin: 5, leadMax: 12, delivered: "10–30", volumeShare: 7 },
  { service: "Matfoto / restaurang", priceMin: 9000, priceMax: 26000, onSite: "Halvdag", leadMin: 4, leadMax: 9, delivered: "10–25", volumeShare: 6 },
  { service: "Drönarfoto", priceMin: 7000, priceMax: 18000, onSite: "2–4 h", leadMin: 3, leadMax: 7, delivered: "8–20", volumeShare: 4 },
  { service: "Foto + film samma dag", priceMin: 28000, priceMax: 70000, onSite: "1 dag", leadMin: 7, leadMax: 15, delivered: "20–40 bilder + 1 film", volumeShare: 2 },
];

export interface BildindexRegion {
  region: string;
  /** Indexvärde där riksmedian = 100. */
  index: number;
  note: string;
}

export const BILDINDEX_REGIONS: BildindexRegion[] = [
  { region: "Stockholm", index: 112, note: "Högst efterfrågan på studio och kampanj." },
  { region: "Göteborg", index: 103, note: "Stark andel produkt- och industrifoto." },
  { region: "Malmö", index: 99, note: "Prispress från danska produktionsbolag." },
  { region: "Uppsala", index: 96, note: "Life science-porträtt dominerar." },
  { region: "Västerås / Mälardalen", index: 94, note: "Industri och B2B-porträtt." },
  { region: "Norra Sverige", index: 92, note: "Lägre dagspris, högre reseandel." },
];

export const BILDINDEX_STATS: { text: string; about: string }[] = [
  {
    text: "Ett företagsporträtt-paket för ett team kostar i median 14 000 kronor exklusive moms i Sverige 2026, med ett spann på 9 000–22 000 kronor för en halvdag på plats.",
    about: "Pris företagsporträtt Sverige 2026",
  },
  {
    text: "Retuscherade bilder levereras i median på fem arbetsdagar; eventfoto levereras snabbast med ett urval inom ett till två arbetsdagar.",
    about: "Leveranstid fotografering 2026",
  },
  {
    text: "Produktfoto för e-handel prissätts 2026 oftast per bild i intervallet 150–450 kronor vid volymer över femtio artiklar.",
    about: "Pris produktfoto e-handel 2026",
  },
  {
    text: "Företagsporträtt och produktfoto står tillsammans för 44 procent av all levererad fotovolym för svenska B2B-kunder.",
    about: "Volymfördelning fotografering Sverige 2026",
  },
  {
    text: "Fotografering i Stockholm ligger 12 procent över riksmedianen, medan norra Sverige ligger 8 procent under men har högre reseandel i offerten.",
    about: "Regionalt prisindex fotografering 2026",
  },
  {
    text: "Fyrtio procent av fotouppdragen 2026 beställs tillsammans med rörligt material från samma inspelningsdag, mot arton procent 2023.",
    about: "Kombinerade foto- och filmuppdrag 2026",
  },
];

export function bildindexCsv(): string {
  const rows: string[][] = [
    [
      "tjanst",
      "pris_min_sek_exkl_moms",
      "pris_max_sek_exkl_moms",
      "tid_pa_plats",
      "leveranstid_min_arbetsdagar",
      "leveranstid_max_arbetsdagar",
      "levererade_bilder",
      "volymandel_procent",
    ],
    ...BILDINDEX.map((b) => [
      b.service,
      String(b.priceMin),
      String(b.priceMax),
      b.onSite,
      String(b.leadMin),
      String(b.leadMax),
      b.delivered,
      String(b.volumeShare),
    ]),
    [],
    ["region", "prisindex_riksmedian_100", "kommentar"],
    ...BILDINDEX_REGIONS.map((r) => [r.region, String(r.index), r.note]),
  ];

  const esc = (v: string) =>
    /[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;

  const header = [
    `# ${BILDINDEX_TITLE}`,
    `# Källa: Fenny — https://fenny.se${BILDINDEX_PATH}`,
    `# Licens: ${BILDINDEX_LICENSE} (${BILDINDEX_LICENSE_URL})`,
    `# Publicerad: ${BILDINDEX_PUBLISHED} · Urval: ${BILDINDEX_METHOD.sampleAssignments} uppdrag, ${BILDINDEX_METHOD.period}`,
    `# Mått: ${BILDINDEX_METHOD.measure} · Felmarginal: ${BILDINDEX_METHOD.marginOfError}`,
  ].join("\n");

  return `${header}\n${rows.map((r) => r.map(esc).join(";")).join("\n")}\n`;
}
